import React from "react";
import RevenueBarChart from "./RevenueBarChart";
import FinancialGraph from "./FinancialGraph";
import type { YearMonthData } from "../../types/core";
import { toDisplayCurrency } from "../../utils/formatters";

// Locked: full English month names. Hardcoded per Bug #16 (locale-safe).
const MONTH_LABELS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

interface YearMonthRowProps {
  month: YearMonthData;
  year: number;
}

const YearMonthRow: React.FC<YearMonthRowProps> = ({ month, year }) => {
  const monthLabel = `${MONTH_LABELS_FULL[month.month_num - 1]} ${year}`;
  // Locked separator: U+00B7 middle dot (matches "Performance & Pipeline · TREND" precedent).
  // monthLabel passed to RevenueBarChart stays free of slice context — chart's own copy
  // doesn't reference month name.
  const headerLabel = month.slice_label ? `${monthLabel} · ${month.slice_label}` : monthLabel;
  return (
    <div data-testid="year-month-row" className="space-y-2">
      <div className="token-10 uppercase text-glass-muted tracking-[0.1em]">{headerLabel}</div>
      <div className="flex flex-col lg:flex-row gap-3 items-stretch overflow-hidden">
        <div className="flex-shrink-0 w-full lg:w-auto">
          <RevenueBarChart
            revenueClosed={month.revenue_closed}
            cashCollected={month.cash_collected}
            highTicketCash={month.high_ticket_cash}
            lowTicketCash={month.low_ticket_cash}
            installmentsCash={month.installments_collected}
            goalAmount={month.goal_amount ?? 0}
            daysElapsed={month.days_elapsed}
            daysInMonth={month.days_in_month}
            monthLabel={monthLabel}
          />
        </div>
        <div className="flex-1 min-w-0 min-h-[260px] overflow-hidden">
          <FinancialGraph
            data={month.daily.map((d) => ({
              date: d.date, // 'MM/DD' — FinancialGraph.formatDateLabel splits on '/' and parseInt's the parts; both 'MM/DD' and 'M/D' parse correctly (FinancialGraph.tsx:60-67).
              cash: d.cash,
              revenue: d.revenue,
              label: d.cash > 0 ? formatCompactValue(d.cash) : "",
            }))}
            goalAmount={month.goal_amount ?? undefined}
            daysInMonth={month.days_in_month}
          />
        </div>
      </div>
    </div>
  );
};

function formatCompactValue(val: number): string {
  const d = toDisplayCurrency(val); // cash (money) — exec USD->AUD toggle
  if (d >= 1000) return `${(d / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return d.toFixed(0);
}

export default YearMonthRow;
